import { useState } from "react";
import {
  ArrowLeft,
  Cloud,
  Server,
  Database,
  HardDrive,
  Monitor,
  Shield,
  Check,
  Info,
  Activity,
} from "lucide-react";
import { SiAmazon } from "react-icons/si";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/common-ui/card";
import { Badge } from "../../../ui/common-ui/badge";
import { useLocation, useNavigate } from "react-router-dom";
import { NGROK } from "../../../../APIs";
import { myLocalStorage } from "../../../../../src/components/StorageHelper";
import axios from "axios";
import { useQuery } from "react-query";
import { FaGoogle } from "react-icons/fa6";
import { VscAzure } from "react-icons/vsc";
import { Button } from "../../../ui/common-ui/button";
import "../CloudStatus.css";
import "../../CloudAnalytics/CloudEntityAnalytics.css";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/common-ui/table";
import { toast } from "sonner";
import CloudRecentActivity from "../../CloudAnalytics/CloudRecentActivity";
type CloudProvider = "gcp" | "aws" | "azure";

interface LocationState {
  resource: any;
  cloudProvider: CloudProvider;
  projectId: string;
  tenantName: string;
  resourceName: string;
  resourceType: string;
}

// Props interface for when component is rendered directly
interface CloudResourceAnalyticsProps {
  cloudProvider?: CloudProvider;
  resourceId?: string;
  resourceName?: string;
  resourceType?: string;
  projectId?: string;
  tenantName?: string;
  resource?: any;
  onBack?: () => void;
  flushHorizontalPadding?: boolean;
}

// Type for resolutions data: entity -> roleToDetach -> rolesToAttach[]
type ResolutionsData = Record<string, Record<string, string[]>>;

const cloudConfig: Record<string, { name: string; icon: any; color: string }> = {
  gcp: {
    name: "GCP",
    icon: FaGoogle,
    color: "text-blue-600",
  },
  aws: {
    name: "AWS",
    icon: SiAmazon,
    color: "text-orange-600",
  },
  azure: {
    name: "Azure",
    icon: VscAzure,
    color: "text-blue-500",
  },
};

const getResourceIcon = (type: string, cloudProvider: string) => {
  if (cloudProvider === "aws") {
    if (type === "ec2" || type === "EC2") return Server;
    if (type === "s3" || type === "S3") return Cloud;
    if (type === "rds" || type === "RDS") return Database;
  }
  if (type === "vm" || type === "VIRTUAL_MACHINE") return Monitor;
  if (type === "storage" || type === "STORAGE_ACCOUNT") return HardDrive;
  if (type === "database" || type === "DATABASE") return Database;
  return Cloud;
};

export default function CloudResourceAnalytics(props: CloudResourceAnalyticsProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  // Tab state
  const [activeTab, setActiveTab] = useState<"details" | "activity">("details");

  // Use props if provided, otherwise fall back to location.state
  const cloudProvider = props.cloudProvider || state?.cloudProvider;
  const projectId = props.projectId || state?.projectId;
  const tenantName = props.tenantName || state?.tenantName;
  const resourceName = props.resourceName || state?.resourceName;
  const resourceType = props.resourceType || state?.resourceType;
  const resource = props.resource || state?.resource;
  const latestSubListRaw = myLocalStorage.getItem("latestSubList");
  const subscriptionName = latestSubListRaw
    ? (typeof latestSubListRaw === "string" ? JSON.parse(latestSubListRaw) : latestSubListRaw)?.subscriptionName
    : undefined;

  const CloudIcon = cloudConfig[cloudProvider]?.icon ?? Cloud;
  const ResourceIcon = getResourceIcon(resourceType, cloudProvider);

  // Handle back navigation
  const handleBack = () => {
    if (props.onBack) {
      props.onBack();
    } else {
      navigate(-1);
    }
  };

  // Fetch resource analytics data
  const { data: resourceAnalytics = {}, isLoading } = useQuery({
    queryKey: [
      "resource-analytics",
      cloudProvider,
      projectId,
      resourceName,
      tenantName,
    ],
    queryFn: async () => {
      let apiUrl = "";

      if (cloudProvider === "gcp") {
        apiUrl = `${NGROK}/api/gcp/analytics/get-resource-analytics-data?tenantName=${tenantName}&projectId=${projectId}&timeRangeCode=9&resourceName=${encodeURIComponent(resourceName)}`;
      } else if (cloudProvider === "azure") {
        apiUrl = `${NGROK}/api/azure/analytics/get-resource-analytics-data?tenantName=${tenantName}&subscriptionId=${projectId}&timeRangeCode=9&resourceId=${encodeURIComponent(resourceName)}`;
      } else if (cloudProvider === "aws") {
        apiUrl = `${NGROK}/api/aws/analytics/get-resource-analytics-data?tenantName=${tenantName}&accountId=${projectId}&timeRangeCode=9&resourceArn=${encodeURIComponent(resourceName)}`;
      }

      const { data } = await axios.get(apiUrl);
      return data || {};
    },
    enabled: !!cloudProvider && !!projectId && !!resourceName && !!tenantName,
  });

  // Fetch resource analytics resolutions
  const { data: resolutionsData = {}, isLoading: isLoadingResolutions } = useQuery<ResolutionsData>({
    queryKey: [
      "resource-analytics-resolutions",
      cloudProvider,
      projectId,
      resourceName,
      tenantName,
    ],
    queryFn: async () => {
      let apiUrl = "";

      if (cloudProvider === "gcp") {
        apiUrl = `${NGROK}/api/gcp/analytics/get-resource-analytics-resolutions?tenantName=${tenantName}&projectId=${projectId}&timeRangeCode=9&resourceName=${encodeURIComponent(resourceName)}`;
      } else if (cloudProvider === "azure") {
        apiUrl = `${NGROK}/api/azure/analytics/get-resource-analytics-resolutions?tenantName=${tenantName}&subscriptionId=${projectId}&timeRangeCode=9&resourceId=${encodeURIComponent(resourceName)}`;
      } else if (cloudProvider === "aws") {
        apiUrl = `${NGROK}/api/aws/analytics/get-resource-analytics-resolutions?tenantName=${tenantName}&accountId=${projectId}&timeRangeCode=9&resourceArn=${encodeURIComponent(resourceName)}`;
      }

      const { data } = await axios.get(apiUrl);
      return data || {};
    },
    enabled: !!cloudProvider && !!projectId && !!resourceName && !!tenantName,
  });

  // Handle resolve button click
  const handleResolve = (entity: string, currentRole: string, suggestedRoles: string[]) => {
    toast.success(`Resolution initiated for ${entity}`, {
      description: `Converting ${currentRole} to ${suggestedRoles.join(", ")}`,
    });
    // TODO: Implement actual resolution API call here
  };

  // Process resolutions data into flat rows for table display
  const resolutionRows: { entity: string; currentRole: string; suggestedRoles: string[] }[] = [];
  Object.entries(resolutionsData).forEach(([entity, rolesMap]) => {
    Object.entries(rolesMap).forEach(([currentRole, suggestedRoles]) => {
      resolutionRows.push({
        entity,
        currentRole,
        suggestedRoles,
      });
    });
  });

  // Process data for charts
  const usersWithActivity = Object.entries(resourceAnalytics).map(([user, permissions]) => {
    const totalActions = Object.values(permissions as Record<string, number>).reduce(
      (sum: number, val) => sum + Number(val),
      0
    );
    return { user, permissions: permissions as Record<string, number>, totalActions };
  });

  const sortedUsers = usersWithActivity.sort((a, b) => b.totalActions - a.totalActions);
  const topUsers = sortedUsers.slice(0, 10);

  const totalUserActivity = usersWithActivity.reduce((sum, u) => sum + u.totalActions, 0);

  // Get all permissions across all users
  const allPermissions: Record<string, number> = {};
  Object.values(resourceAnalytics).forEach((permissions: any) => {
    Object.entries(permissions).forEach(([perm, count]) => {
      allPermissions[perm] = (allPermissions[perm] || 0) + Number(count);
    });
  });

  const topPermissions = Object.entries(allPermissions)
    .sort(([, a], [, b]) => Number(b) - Number(a))
    .slice(0, 10);

  const totalPermissionUsage = Object.values(allPermissions).reduce(
    (sum, val) => sum + val,
    0
  );

  const donutColors = [
    "#3B82F6",
    "#10B981",
    "#F59E0B",
    "#8B5CF6",
    "#EC4899",
    "#F97316",
    "#06B6D4",
    "#84CC16",
    "#EAB308",
    "#A855F7",
  ];

  // Check if we have valid data (from props or state)
  if (!cloudProvider && !state) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6">
        <p className="text-muted-foreground mb-4">No resource data available</p>
        <Button onClick={handleBack}>Go Back</Button>
      </div>
    );
  }

  return (
    <div className={`cea-page ${props.flushHorizontalPadding ? "cea-page-flush-horizontal" : ""}`}>
      {/* Resource Info Card */}
      <div className="cea-header">
        <div className="cea-entity-row">
          <button
            className="cea-back-icon-btn"
            onClick={handleBack}
            data-testid="button-back"
            aria-label="Back"
            title="Back"
          >
            <ArrowLeft />
          </button>

          <div className="cea-avatar">
            <ResourceIcon />
          </div>

          <div className="cea-entity-info">
            <div className="flex items-center gap-2 mb-2">
              <CloudIcon className={`w-5 h-5 ${cloudConfig[cloudProvider]?.color ?? "text-gray-400"}`} />
              <div className="cea-entity-name">
                {resource?.name || resourceName}
              </div>
            </div>
            <div className="cea-entity-sub">
              <span className="cea-badge provider">{resourceType}</span>
              {resource?.status && (
                <span className="cea-role-chip">{resource.status}</span>
              )}
              {resource?.region && (
                <span className="cea-role-chip">{resource.region}</span>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 self-start ml-auto flex flex-col items-end gap-2">
            <div className="bg-muted/30 border border-border/40 rounded-lg px-3 py-1.5 text-[12px] text-muted-foreground flex items-center gap-1.5 shadow-sm transition-colors hover:bg-muted/50">
              <span className="font-medium text-foreground/80">
                {cloudProvider === "gcp"
                  ? "Project"
                  : cloudProvider === "azure"
                    ? "Subscription"
                    : "Account"}:
              </span>
              <span className="text-foreground/90 break-all max-w-[320px]">{projectId}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="cea-tabs cra-tabs">
        <button
          onClick={() => setActiveTab("details")}
          className={`cea-tab ${activeTab === "details" ? "active" : ""}`}
          data-testid="tab-details"
        >
          <Info className="w-4 h-4" />
          Details
        </button>

        <button
          onClick={() => setActiveTab("activity")}
          className={`cea-tab ${activeTab === "activity" ? "active" : ""}`}
          data-testid="tab-activity"
        >
          <Activity className="w-4 h-4" />
          Recent Activity
        </button>
      </div>

      {/* DETAILS TAB */}
      {activeTab === "details" && (
        <>
          {/* Analytics Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Loading analytics...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Top Users + Top Permissions */}
              <div className="grid grid-cols-2 gap-6">
                {/* Top Users - donut */}
                <Card className="cloud-card-surface rounded-2xl overflow-hidden">
                  <CardHeader className="border-b pb-4">
                    <CardTitle className="text-base font-medium">
                      Top Users
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Users who accessed this resource the most
                    </p>
                  </CardHeader>
                  <CardContent className="py-8">
                    <div className="flex flex-col items-center gap-6">
                      <div className="relative w-56 h-56">
                        <svg viewBox="0 0 200 200" className="transform -rotate-90">
                          {topUsers.map(({ user, totalActions }, idx) => {
                            const percentage = (totalActions / totalUserActivity) * 100;

                            const prevPercentages = topUsers
                              .slice(0, idx)
                              .reduce(
                                (sum, u) => sum + (u.totalActions / totalUserActivity) * 100,
                                0
                              );

                            const circumference = 2 * Math.PI * 70;
                            const offset = circumference * (1 - percentage / 100);
                            const rotation = (prevPercentages / 100) * 360;

                            return (
                              <circle
                                key={user}
                                cx="100"
                                cy="100"
                                r="70"
                                fill="none"
                                stroke={donutColors[idx]}
                                strokeWidth="32"
                                strokeDasharray={circumference}
                                strokeDashoffset={offset}
                                style={{
                                  transformOrigin: "100px 100px",
                                  transform: `rotate(${rotation}deg)`,
                                }}
                              />
                            );
                          })}
                        </svg>

                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            {totalUserActivity !== 0 ? (
                              <>
                                <div className="text-2xl font-bold">
                                  {totalUserActivity.toLocaleString()}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  Total Access
                                </div>
                              </>
                            ) : (
                              <div className="text-xs text-muted-foreground mt-1">
                                No data found
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="w-full space-y-2.5">
                        {topUsers.map(({ user, totalActions }, idx) => {
                          const percentage = (totalActions / totalUserActivity) * 100;

                          return (
                            <div
                              key={user}
                              className="flex items-center gap-3 group cursor-default"
                              title={`${totalActions.toLocaleString()} accesses`}
                            >
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: donutColors[idx] }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm truncate font-medium">{user}</div>
                              </div>
                              <div className="flex-shrink-0">
                                <span className="text-sm font-semibold">
                                  {percentage.toFixed(1)}%
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Top Permissions - styled like CloudEntityAnalytics */}
                <Card className="cloud-card-surface rounded-2xl overflow-hidden">
                  <CardHeader className="border-b pb-4">
                    <CardTitle className="text-base font-medium">
                      Top Permissions
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Most frequently used permissions on this resource
                    </p>
                  </CardHeader>
                  <CardContent className="py-6">
                    <div className="space-y-3">
                      {topPermissions.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No permission data available
                        </p>
                      ) : (
                        topPermissions.map(([permission, count]) => {
                          const percentage = (Number(count) / totalPermissionUsage) * 100;

                          return (
                            <div
                              key={permission}
                              className="space-y-1.5 cursor-default"
                              title={`${Number(count).toLocaleString()} uses`}
                            >
                              <div className="flex items-baseline justify-between gap-3">
                                <div className="text-sm font-medium font-mono truncate flex-1">
                                  {permission}
                                </div>
                                <div className="flex-shrink-0">
                                  <span className="text-sm font-semibold">
                                    {percentage.toFixed(1)}%
                                  </span>
                                </div>
                              </div>
                              <div className="bg-muted rounded-sm h-2 overflow-hidden">
                                <div
                                  className="bg-primary h-full rounded-sm transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed User Activity List */}
              <Card className="cloud-card-surface rounded-2xl overflow-hidden">
                <CardHeader className="border-b pb-4">
                  <CardTitle className="text-base font-medium">
                    Detailed User Activity
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Complete breakdown of user actions on this resource
                  </p>
                </CardHeader>
                <CardContent className="py-6">
                  {sortedUsers.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No user activity data available
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {sortedUsers.map(({ user, permissions, totalActions }) => (
                        <div key={user} className="cloud-list-card rounded-2xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-xs font-medium text-primary">
                                  {user.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium">{user}</p>
                                <p className="text-xs text-muted-foreground">
                                  {totalActions.toLocaleString()} total actions
                                </p>
                              </div>
                            </div>
                            <Badge variant="secondary">{Object.keys(permissions).length} permissions</Badge>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(permissions)
                              .sort(([, a], [, b]) => Number(b) - Number(a))
                              .slice(0, 5)
                              .map(([perm, count]) => (
                                <Badge key={perm} variant="outline" className="text-xs">
                                  {perm}: {Number(count).toLocaleString()}
                                </Badge>
                              ))}
                            {Object.keys(permissions).length > 5 && (
                              <Badge variant="secondary" className="text-xs">
                                +{Object.keys(permissions).length - 5} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Resolve Project Level Roles */}
              {(isLoadingResolutions || resolutionRows.length > 0) && (
                <Card className="cloud-card-surface rounded-2xl overflow-hidden">
                  <CardHeader className="border-b pb-4">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary" />
                      <CardTitle className="text-base font-medium">
                        Resolve Project Level Roles
                      </CardTitle>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Entities with project-level roles that can be converted to resource-level roles with lesser privileges
                    </p>
                  </CardHeader>
                  <CardContent className="py-6">
                    {isLoadingResolutions ? (
                      <div className="flex items-center justify-center py-8">
                        <p className="text-muted-foreground">Loading resolutions...</p>
                      </div>
                    ) : (
                      <div className="cloud-card-surface rounded-2xl overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-semibold">Entity</TableHead>
                              <TableHead className="font-semibold">Current Role</TableHead>
                              <TableHead className="font-semibold">Suggested Role(s)</TableHead>
                              <TableHead className="font-semibold text-right">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {resolutionRows.map((row, index) => (
                              <TableRow key={`${row.entity}-${row.currentRole}-${index}`}>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                      <span className="text-xs font-medium text-primary">
                                        {row.entity.charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                    <span className="font-medium text-sm truncate max-w-[200px]" title={row.entity}>
                                      {row.entity}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="destructive" className="text-xs">
                                    {row.currentRole}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-1">
                                    {row.suggestedRoles.map((role, roleIndex) => (
                                      <Badge key={roleIndex} variant="secondary" className="text-xs">
                                        {role}
                                      </Badge>
                                    ))}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    size="sm"
                                    onClick={() => handleResolve(row.entity, row.currentRole, row.suggestedRoles)}
                                    className="gap-1"
                                  >
                                    <Check className="w-3 h-3" />
                                    Resolve
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}

      {/* ACTIVITY TAB */}
      {activeTab === "activity" && (
        <CloudRecentActivity
          url={`${NGROK}/api/cloud/get-resource-raw-logs?tenantName=${tenantName}&cloudId=${projectId}&entity=${encodeURIComponent(resourceName)}&cloudType=${cloudProvider.toUpperCase()}`}
          tenantName={tenantName}
          cloudType={cloudProvider.toUpperCase()}
          subscriptionName={subscriptionName}
          resourceAnalytics
        />
      )}
    </div>
  );
}
